import { useId } from 'react'
import { memoriserPays, normaliserTelephone, trouverPays, type Pays, type ResultatNumero } from './api'
import { SelecteurPays } from './SelecteurPays'
import { injecterStyles } from './styles'

export interface ValeurTelephone {
  /** Texte tel que saisi. */
  texte: string
  /** Code ISO du pays du sélecteur. */
  iso: string
}

/**
 * Résultat de validation d'une valeur. `e164` est le numéro à envoyer aux edge functions / à Supabase, ou null si la
 * saisie est vide ou invalide (`resultat` dit pourquoi).
 */
export function lireTelephone(v: ValeurTelephone): { e164: string | null; resultat: ResultatNumero | null } {
  if (!v.texte.trim()) return { e164: null, resultat: null }
  const r = normaliserTelephone(v.texte, v.iso)
  return { e164: r.ok ? r.e164 : null, resultat: r }
}

/**
 * Champ « numéro de téléphone » : sélecteur de pays (nom + indicatif, avec recherche) + saisie libre.
 * Le pays de départ vient de la configuration (voir `lirePaysDefaut`) — jamais écrit en dur ici. Coller « +33 6 12 34 56 78 »
 * dans un champ réglé sur le Mali est reconnu comme un numéro français (le module de normalisation lit l'indicatif).
 * L'erreur n'est affichée qu'une fois le champ quitté ou si `montrerErreur` est vrai (pas de rouge pendant la frappe).
 */
export function ChampTelephone({
  valeur,
  surChangement,
  etiquette = 'Numéro WhatsApp',
  montrerErreur = false,
  desactive = false,
  autoFocus = false,
  aide,
  id,
}: {
  valeur: ValeurTelephone
  surChangement: (v: ValeurTelephone) => void
  etiquette?: string
  montrerErreur?: boolean
  desactive?: boolean
  autoFocus?: boolean
  /** Phrase d'aide sous le champ (facultatif). */
  aide?: string
  id?: string
}) {
  injecterStyles()
  const idAuto = useId()
  const idChamp = id ?? idAuto
  const pays = trouverPays(valeur.iso)
  const { resultat } = lireTelephone(valeur)
  const erreur = montrerErreur && resultat && !resultat.ok ? resultat.message : null

  function surPays(p: Pays) {
    memoriserPays(p.iso)
    surChangement({ ...valeur, iso: p.iso })
  }

  return (
    <div className="cw-champ">
      <label className="cw-etiquette" htmlFor={idChamp}>{etiquette}</label>
      <div className="cw-ligne">
        <SelecteurPays iso={valeur.iso} surChoix={surPays} desactive={desactive} />
        <input
          id={idChamp}
          className="cw-entree"
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          autoFocus={autoFocus}
          disabled={desactive}
          placeholder={pays?.exemple ?? ''}
          value={valeur.texte}
          aria-invalid={erreur ? true : undefined}
          aria-describedby={`${idChamp}-aide`}
          onChange={(e) => surChangement({ ...valeur, texte: e.target.value })}
        />
      </div>
      <p id={`${idChamp}-aide`} className={erreur ? 'cw-erreur' : 'cw-aide'} role={erreur ? 'alert' : undefined}>
        {erreur ?? aide ?? ''}
      </p>
    </div>
  )
}
